import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  confirmSignIn,
  signOut,
} from 'aws-amplify/auth';
import { logger } from '../utils/logger';
import { clearUserProfile } from '../utils/userProfileStorage';

class AuthService {
  async signOut() {
    try {
      clearUserProfile();
      const result  = await signOut();
      //console.log('Sign out result:', result);
    } catch (error) {
      console.error('Sign out error:', error);
      throw new Error('Failed to sign out. Please try again.');
    }
  }
  async signUp(phoneNumber, email) {
    try {
      let username, deliveryMedium, _preferredChallenge, password, attributes = {};
      if (email) {
        username = email;
        deliveryMedium = 'EMAIL';
        _preferredChallenge = 'EMAIL_OTP';
        attributes = { email: email };
      } else if (phoneNumber) {
        username = this.formatPhoneNumber(phoneNumber);
        deliveryMedium = 'SMS';
        _preferredChallenge = 'SMS_OTP';
          attributes = { phone_number: username };
      } else {
        throw new Error('No phone number or email provided for sign up.');
      }
      password = this.generatePassword(username);
      logger.log('Attempting sign up with username:', username);
      logger.log('Attempting sign up with password:', password);
      const { userId, nextStep } = await signUp({
        username,
        //password,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          preferredChallenge: _preferredChallenge,
        },
      });
      const signUpStep = nextStep.signUpStep;
      const codeDeliveryDetails = {
        deliveryMedium: nextStep.codeDeliveryDetails?.deliveryMedium ?? (email ? 'EMAIL' : 'SMS'),
        destination: nextStep.codeDeliveryDetails?.destination ?? username,
      };
      logger.log('SignUp nextStep:', signUpStep);
      logger.log('SignUp deliveryMedium:', codeDeliveryDetails.deliveryMedium);
      logger.log('SignUp destination:', codeDeliveryDetails.destination);
      return {
        userId: userId || '',
        nextStep: {
          signUpStep,
          codeDeliveryDetails,
        },
      };
    } catch (error) {
      console.error('Sign up error:', error);
      throw this.handleAuthError(error);
    }
  }

  async confirmSignIn(phoneNumber, email, code) {
    try {
      let username;
      if (email) {
        username = email;
      } else if (phoneNumber) {
        username = this.formatPhoneNumber(phoneNumber);
      } else {
        throw new Error('No phone number or email provided for confirmation.');
      }
      const result = await confirmSignIn({ username, challengeResponse: code });
      logger.log('Confirm sign in result0:', JSON.stringify(result));
      logger.log('Confirm sign in result01:', result);
      logger.log('Confirm sign in result1:', result?.isSignedIn);
      
      if(!result?.isSignedIn)
      {
        return {isSignedIn: false};
      }
      else {
        return result;
      }
    } catch (error) {
      console.error('Confirm sign in error:', error);
      throw this.handleAuthError(error);
    }
  }

  async confirmSignUp(phoneNumber, email, code) {
    try {
      let username;
      if (email) {
        username = email;
      } else if (phoneNumber) {
        username = this.formatPhoneNumber(phoneNumber);
      } else {
        throw new Error('No phone number or email provided for confirmation.');
      }
      await confirmSignUp({ username, confirmationCode: code });
    } catch (error) {
      console.error('Confirm sign up error:', error);
      throw this.handleAuthError(error);
    }
  }

  async resendSignUpCode(phoneNumber, email) {
    try {
      let username;
      if (email) {
        username = email;
      } else if (phoneNumber) {
        username = this.formatPhoneNumber(phoneNumber);
      } else {
        throw new Error('No phone number or email provided for resending code.');
      }
      logger.log('Resending sign up code to:', username);
      await resendSignUpCode({ username });
    } catch (error) {
      console.error('Resend code error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * 
   * Only For Phone Number Sign In
   * @param {*} phoneNumber 
   * @returns 
   */
  async signIn(phoneNumber) {
    try {
      const formattedPhone = this.formatPhoneNumber(phoneNumber);
      logger.log('Attempting sign in with phone:', formattedPhone);
      const { isSignedIn, nextStep } = await signIn({
        username: formattedPhone,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          preferredChallenge: 'SMS_OTP',  //EMAIL_OTP
        },
      });
      logger.log('Sign in response:', { isSignedIn, nextStep: nextStep.signInStep });
      if (isSignedIn) {
        return {
          isSignedIn: true,
          nextStep: {
            signInStep: 'DONE',
            codeDeliveryDetails: {
              deliveryMedium: 'SMS',
              destination: formattedPhone,
            },
          },
        };
      }
      const signInStep = nextStep.signInStep;
      if (signInStep && (signInStep.includes('CONFIRM_SIGN_IN') || signInStep.includes('CONFIRM_SIGN_IN_WITH_SMS_CODE'))) {
        return {
          isSignedIn: false,
          nextStep: {
            signInStep: signInStep,
            codeDeliveryDetails: {
              deliveryMedium: nextStep.codeDeliveryDetails?.deliveryMedium || 'SMS',
              destination: nextStep.codeDeliveryDetails?.destination || formattedPhone,
            },
          },
        };
      }
      return {
        isSignedIn: false,
        nextStep: {
          signInStep: signInStep,
          codeDeliveryDetails: {
            deliveryMedium: 'SMS',
            destination: formattedPhone,
          },
        },
      };
    } catch (error) {
      console.error('Sign in error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        error: error,
      });
      if (error.name === 'NotAuthorizedException') {
        throw new Error('Invalid credentials. Please check your phone number and try again.');
      }
      if (error.name === 'UserNotFoundException') {
        throw new Error('No account found with this phone number. Please sign up first.');
      }
      throw this.handleAuthError(error);
    }
  }

  async signInEmail(email) {
    try {
      const formattedEmail = email?.trim().toLowerCase();
      logger.log('Attempting sign in with email:', formattedEmail);
      const { isSignedIn, nextStep } = await signIn({
        username: formattedEmail,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          preferredChallenge: 'EMAIL_OTP',  //SMS_OTP
        },
      });
      logger.log('Sign in response:', { isSignedIn, nextStep: nextStep.signInStep });
      if (isSignedIn) {
        return {
          isSignedIn: true,
          nextStep: {
            signInStep: 'DONE',
            codeDeliveryDetails: {
              deliveryMedium: 'EMAIL',
              destination: formattedEmail,
            },
          },
        };
      }
      const signInStep = nextStep.signInStep;
      if (signInStep && (signInStep.includes('CONFIRM_SIGN_IN') || signInStep.includes('CONFIRM_SIGN_IN_WITH_EMAIL_CODE'))) {
        return {
          isSignedIn: false,
          nextStep: {
            signInStep: signInStep,
            codeDeliveryDetails: {
              deliveryMedium: nextStep.codeDeliveryDetails?.deliveryMedium || 'EMAIL',
              destination: nextStep.codeDeliveryDetails?.destination || formattedEmail,
            },
          },
        };
      }
      return {
        isSignedIn: false,
        nextStep: {
          signInStep: signInStep,
          codeDeliveryDetails: {
            deliveryMedium: 'EMAIL',
            destination: formattedEmail,
          },
        },
      };
    } catch (error) {
      console.error('Sign in error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        error: error,
      });
      if (error.name === 'NotAuthorizedException') {
        throw new Error('Invalid credentials. Please check your email and try again.');
      }
      if (error.name === 'UserNotFoundException') {
        throw new Error('No account found with this email. Please sign up first.');
      }
      throw this.handleAuthError(error);
    }
  }

  async getAuthTokens() {
    try {
      const session = await fetchAuthSession();
      //logger.log('Auth session fetched:', session);
      if (session.tokens) {
        return {
          accessToken: session.tokens.accessToken?.toString() || '',
          idToken: session.tokens.idToken?.toString() || '',
          refreshToken: session.tokens.refreshToken?.toString() || '',
          identityId: session.identityId || '',
        };
      }
      return null;
    } catch (error) {
      console.error('Get auth tokens error:', error);
      return null;
    }
  }

  async getAuthTokensFull() {
    try {
      const session = await fetchAuthSession();
      //logger.log('Auth session fetched:', session);
      if (session.tokens) {
        return session;
      }
      return null;
    } catch (error) {
      console.error('Get auth tokens error:', error);
      return null;
    }
  }

  async getCurrentUserDetails() {
    try {
      const { userId, username } = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      return {
        userId,
        username: username || userId,
        phoneNumber: attributes.phone_number,
        email: attributes.email,
      };
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  async checkAuthStatus() {
    try {
      const result = await getCurrentUser();
      //console.log('checkAuthStatus - current user:', result);
      if (result && result.userId)
      {
        return true;
      }
      else {
        return false;
      }
    } catch (error) {
      console.error('Check auth status error:', error);
      return false;
    }
  }

  formatPhoneNumber(phoneNumber) {
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');
    if (!cleaned.startsWith('+')) {
      if (cleaned.startsWith('0')) {
        cleaned = cleaned.substring(1);
      }
      cleaned = `+91${cleaned}`;
    }
    return cleaned;
  }

  generatePassword(identifier) {
    // Always generate a password with at least one lowercase, one uppercase, one digit, and one special character
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const special = '!@#$%^&*';
    const all = lower + upper + digits + special;
    const length = 16;
    let password = '';
    // Ensure at least one of each required type
    password += lower[Math.floor(Math.random() * lower.length)];
    password += upper[Math.floor(Math.random() * upper.length)];
    password += digits[Math.floor(Math.random() * digits.length)];
    password += special[Math.floor(Math.random() * special.length)];
    // Fill the rest randomly
    for (let i = 4; i < length; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }
    // Shuffle password to avoid predictable positions
    password = password.split('').sort(() => 0.5 - Math.random()).join('');
    return password;
  }

  handleAuthError(error) {
    let message = 'An authentication error occurred';
    if (error.name === 'UsernameExistsException') {
      message = 'This phone number/email is already registered. Please sign in instead.';
    } else if (error.name === 'UserNotFoundException') {
      message = 'No account found with this phone number/email. Please sign up first.';
    } else if (error.name === 'NotAuthorizedException') {
      if (error.message && error.message.includes('unauthorized attribute')) {
        message = 'App client does not have permission to write user attributes. Please contact support.';
      } else if (error.message && error.message.includes('Invalid session for the user')) {
        message = 'Your OTP Code has expired. Please request a new one.';
      } else {
        message = 'Invalid credentials. Please try again.';
      }
    } else if (error.name === 'CodeMismatchException') {
      message = 'Invalid verification code. Please try again.';
    } else if (error.name === 'ExpiredCodeException') {
      message = 'Verification code has expired. Please request a new one.';
    } else if (error.name === 'LimitExceededException') {
      message = 'Too many attempts. Please try again later.';
    } else if (error.message) {
      message = error.message;
    }
    return new Error(message);
  }
}

export const authService = new AuthService();
